import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Upload,
  Check,
  Circle,
  FolderOpen,
  Download,
  Loader2,
  Trash2,
  Eye,
  RefreshCw,
  Lock,
} from 'lucide-react';
import { supabase, logAuditEvent } from '../lib/supabase';
import { SUPPLEMENTARY_FORM_CODES_SQL } from '../lib/formCodes';
import { useMatter } from '../hooks/useMatter';
import {
  checklistForVisa,
  getSponsorDocumentChecklist,
  CATEGORY_LABELS,
  type DocItem,
  type IntakeAnswers,
} from '../lib/documentChecklist';
import { uploadDocument, UploadValidationError, BUCKET_INTAKE, ACCEPTED_FORMATS_LABEL } from '../lib/uploadDocument';
import DocumentViewer, { statusMeta } from '../components/DocumentViewer';
import { intakeAnswersFromFieldData, type IntakeFieldRow } from '../lib/intakeAnswers';

// Hard byte cap (server-side bucket cap is 50 MB; we keep 25 MB on the UI for a
// gentler error before the storage layer rejects). Image megapixel downscale to
// 5 MP is handled inside uploadDocument().
const MAX_FILE_BYTES = 25 * 1024 * 1024;
// Path-only fallback for legacy `matter-documents` rows (still readable here).
const LEGACY_BUCKET = 'matter-documents';
const ALLOWED_INPUT = 'image/*,application/pdf,.heic,.heif';

type ReviewStatus = 'pending' | 'reviewing' | 'approved' | 'needs_replacement';

interface DocumentRow {
  id: string;
  storage_path: string;
  filename: string;
  document_type: string | null;
  size_bytes: number | null;
  uploaded_at: string;
  mime_type?: string | null;
  review_status?: ReviewStatus;
  review_note?: string | null;
  reviewed_at?: string | null;
}

export default function Documents() {
  const {
    activeMatter,
    contact,
    actuallyStaff,
    impersonatedMatterId,
    impersonatedRole,
    effectiveContactId,
    loading: matterLoading,
  } = useMatter();
  // When staff use the impersonation toggle to preview the client view, hide
  // staff-only review controls so what they see matches what the client sees.
  const showStaffControls = actuallyStaff && !impersonatedMatterId;
  // Sponsor mode = either a real sponsor signed in, OR staff impersonating
  // the sponsor side of a matter. Drives:
  //  - which checklist we render (sponsor-only vs applicant + sponsor-tagged)
  //  - the storage path scheme (/sponsor/<contact_id>/...)
  //  - the documents.uploader_role column on insert
  //  - which rows count toward the progress bar
  const isSponsorMode = impersonatedMatterId
    ? impersonatedRole === 'sponsor'
    : contact?.contact_type === 'sponsor';
  // BUG-1 (Jane 2026-07-23): impersonation preview is strictly read-only. Every
  // upload, replace and remove control is inert; viewing documents stays live.
  // The contact id we upload AS — applicant mode uses the signed-in contact
  // (or impersonated applicant), sponsor mode uses the matter's
  // sponsor_contact_id resolved through useMatter's effectiveContactId.
  const uploadContactId = effectiveContactId ?? contact?.id ?? null;
  const [docs, setDocs] = useState<DocumentRow[]>([]);
  // Map of document_id → latest comment body. The legacy `review_note` column
  // was a single-value field that got stale once we moved feedback to the
  // append-only document_comments table; fetching the latest comment per doc
  // gives the client an accurate "newest update" preview.
  const [latestComment, setLatestComment] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadFor, setUploadFor] = useState<string | null>(null);
  // When set, the next file picked will REPLACE this existing document
  // (chains via documents.previous_version_id; old version preserved for
  // audit). Cleared once the upload completes or the picker is cancelled.
  const [replacingDocId, setReplacingDocId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadInfo, setUploadInfo] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [viewingDocId, setViewingDocId] = useState<string | null>(null);
  const [hasMinorDependents, setHasMinorDependents] = useState(false);
  // Intake answers drive the conditional document-checklist engine. We read the
  // applicant's intake_field_data rows and map the handful of keys the matrix
  // keys off (joint bank account, joint assets, relationship status, dependent
  // child with sponsor, etc.) into a normalised IntakeAnswers object. Missing
  // answers default conservatively (conditional rows stay hidden).
  const [intakeAnswers, setIntakeAnswers] = useState<IntakeAnswers>({});
  // Applicant's stage-1 latch. Until it is set, post-lodgement (stage-2)
  // documents are greyed and excluded from the checklist counter (§1.6).
  const [stage1LatchedAt, setStage1LatchedAt] = useState<string | null>(null);
  // Deep-link support: `?view=<doc_id>` auto-opens the doc viewer for that
  // document. Used by doc-status notification emails so the client lands
  // straight on the rejected file with the staff comment visible.
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!activeMatter) return;
    let cancelled = false;
    async function loadDocs() {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from('documents')
        .select('id, storage_path, filename, document_type, size_bytes, uploaded_at, mime_type, review_status, review_note, reviewed_at, previous_version_id, version_number')
        .eq('matter_id', activeMatter!.id)
        .is('deleted_at', null)
        .order('uploaded_at', { ascending: false });
      if (cancelled) return;
      if (err) {
        setError(err.message);
      } else {
        const rawRows = (data ?? []) as unknown as Array<DocumentRow & { previous_version_id: string | null; version_number: number }>;
        // Show only the "active" version of each doc — i.e. exclude any row
        // that has a successor (another row pointing at it via
        // previous_version_id). Old versions are kept in DB for compliance
        // but the client checklist always shows the latest.
        const supersededIds = new Set<string>();
        for (const r of rawRows) {
          if (r.previous_version_id) supersededIds.add(r.previous_version_id);
        }
        const rows = rawRows.filter((r) => !supersededIds.has(r.id)) as unknown as DocumentRow[];
        setDocs(rows);
        // Fetch latest comment per doc — used for the "From your case
        // manager: …" preview line on each row. Single round-trip; we sort
        // ascending so the LAST item per doc is the newest.
        const ids = rows.map((r) => r.id);
        if (ids.length > 0) {
          const { data: comments } = await supabase
            .from('document_comments')
            .select('document_id, body, created_at')
            .in('document_id', ids)
            .order('created_at', { ascending: true });
          const latest: Record<string, string> = {};
          for (const c of (comments ?? []) as { document_id: string; body: string }[]) {
            latest[c.document_id] = c.body;
          }
          if (!cancelled) setLatestComment(latest);
        }
      }
      setLoading(false);
    }
    loadDocs();
    // Detect minor migrating dependents → controls Form 1229 inclusion.
    (async () => {
      const { data } = await supabase
        .from('matter_contacts')
        .select(
          `is_migrating, contact:contacts!matter_contacts_contact_id_fkey (date_of_birth)`,
        )
        .eq('matter_id', activeMatter!.id)
        .eq('role', 'dependant');
      if (cancelled) return;
      const minor = (data ?? []).some((row) => {
        const r = row as { is_migrating: boolean; contact: { date_of_birth: string | null } | null };
        if (!r.is_migrating) return false;
        const dob = r.contact?.date_of_birth;
        if (!dob) return false;
        const age = Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
        return age < 18;
      });
      setHasMinorDependents(minor);
    })();
    // Load intake answers for the conditional checklist engine. Applicant-
    // owned signals (relationship status, prior relationships, migrating
    // family, etc.) carry the relationship/financial/household triggers in
    // Jane's matrix and live on the matter's APPLICANT intake_form
    // (regardless of who is viewing this page). Sponsor-owned signals
    // (sponsor.previous_relationships.*) are declared on the SPONSOR's own
    // intake_form — a different form_id — per the schema field-ownership
    // registry, so we fetch that one too (best-effort; no sponsor form yet
    // is a normal state, not an error). intakeAnswersFromFieldData hydrates
    // each row set and reads the nested canonical paths.
    (async () => {
      const { data: form } = await supabase
        .from('intake_forms')
        .select('id, stage1_completed_at')
        .eq('matter_id', activeMatter!.id)
        .eq('contact_id', activeMatter!.primary_contact_id)
        .not('form_code', 'in', SUPPLEMENTARY_FORM_CODES_SQL)
        .order('last_saved_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      setStage1LatchedAt(form?.stage1_completed_at ?? null);

      let applicantRows: IntakeFieldRow[] = [];
      if (form?.id) {
        const { data: rows } = await supabase
          .from('intake_field_data')
          .select('field_key, field_value')
          .eq('intake_form_id', form.id);
        if (cancelled) return;
        applicantRows = (rows ?? []) as IntakeFieldRow[];
      }

      let sponsorRows: IntakeFieldRow[] = [];
      if (activeMatter!.sponsor_contact_id) {
        const { data: sponsorForm } = await supabase
          .from('intake_forms')
          .select('id')
          .eq('matter_id', activeMatter!.id)
          .eq('contact_id', activeMatter!.sponsor_contact_id)
          .not('form_code', 'in', SUPPLEMENTARY_FORM_CODES_SQL)
          .order('last_saved_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (sponsorForm?.id) {
          const { data: rows } = await supabase
            .from('intake_field_data')
            .select('field_key, field_value')
            .eq('intake_form_id', sponsorForm.id);
          if (cancelled) return;
          sponsorRows = (rows ?? []) as IntakeFieldRow[];
        }
      }

      setIntakeAnswers(intakeAnswersFromFieldData(applicantRows, sponsorRows));
    })();
    return () => {
      cancelled = true;
    };
  }, [activeMatter]);

  const docsByType = groupByType(docs);

  // Once docs have loaded, honour `?view=<doc_id>` from the URL by opening
  // the viewer modal for that doc. We strip the param afterwards so a refresh
  // doesn't keep re-opening it.
  useEffect(() => {
    if (loading) return;
    const target = searchParams.get('view');
    if (!target) return;
    const exists = docs.some((d) => d.id === target);
    if (exists) {
      setViewingDocId(target);
      const next = new URLSearchParams(searchParams);
      next.delete('view');
      setSearchParams(next, { replace: true });
    }
  }, [loading, docs, searchParams, setSearchParams]);

  const handleUploadClick = (documentType: string) => {
    setUploadFor(documentType);
    setReplacingDocId(null);
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const handleReplaceClick = (documentType: string, replacesDocId: string) => {
    setUploadFor(documentType);
    setReplacingDocId(replacesDocId);
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = ''; // reset so the same file can be re-picked
    // BUG-1: belt-and-braces guard; uploadDocument also refuses in preview.
    if (files.length === 0 || !activeMatter || !uploadContactId || !uploadFor) return;

    setUploadProgress(1);
    setUploadError(null);
    setUploadInfo(null);

    const completed: { from: number; to: number } | null[] = [];
    let infoLine: string | null = null;

    try {
      for (let i = 0; i < files.length; i++) {
        const original = files[i];

        if (original.size > MAX_FILE_BYTES) {
          throw new Error(
            `${original.name} is ${formatBytes(original.size)}, larger than the ${formatBytes(MAX_FILE_BYTES)} per-file cap. Split or compress and try again.`,
          );
        }

        setUploadProgress(Math.max(1, Math.round((i / files.length) * 50)));

        const result = await uploadDocument({
          file: original,
          matterId: activeMatter.id,
          contactId: uploadContactId,
          checklistItem: uploadFor,
          replacesDocumentId: replacingDocId ?? undefined,
          uploaderRole: isSponsorMode ? 'sponsor' : 'applicant',
        });

        setUploadProgress(50 + Math.round(((i + 1) / files.length) * 50));

        // Read back as DocumentRow shape.
        const { data: row } = await supabase
          .from('documents')
          .select('id, storage_path, filename, document_type, size_bytes, uploaded_at, mime_type, review_status, review_note, reviewed_at')
          .eq('id', result.documentId)
          .single();
        if (row) setDocs((prev) => [row as unknown as DocumentRow, ...prev]);

        logAuditEvent('document.upload', {
          tableName: 'documents',
          rowId: result.documentId,
          changes: {
            checklist_item: uploadFor,
            filename: result.filename,
            original_filename: original.name,
            original_size_bytes: original.size,
            heic_converted: result.heicConverted,
            compressed: result.compression !== null,
          },
        }).catch(() => {});

        if (result.compression && !infoLine) {
          infoLine = `Resized from ${formatBytes(result.compression.from)} to ${formatBytes(result.compression.to)} to fit the 5 MP cap.`;
        }
        if (result.heicConverted && !infoLine) {
          infoLine = 'Converted from HEIC to JPG so previews work everywhere.';
        }
        completed.push(null);
      }

      setUploadProgress(100);
      if (infoLine) setUploadInfo(infoLine);
    } catch (err) {
      if (err instanceof UploadValidationError) {
        setUploadError(err.message);
      } else {
        setUploadError(err instanceof Error ? err.message : 'Upload failed.');
      }
    } finally {
      setUploadFor(null);
      setReplacingDocId(null);
      window.setTimeout(() => setUploadProgress(0), 1200);
    }
  };

  const handleDownload = async (doc: DocumentRow) => {
    // Live uploads land in BUCKET_INTAKE; pre-cutover legacy rows live in
    // LEGACY_BUCKET. Try the live bucket first, fall back to legacy.
    let signedUrl: string | null = null;
    let lastErr: string | null = null;
    for (const bucket of [BUCKET_INTAKE, LEGACY_BUCKET]) {
      const { data, error: err } = await supabase.storage
        .from(bucket)
        .createSignedUrl(doc.storage_path, 60);
      if (data?.signedUrl) {
        signedUrl = data.signedUrl;
        break;
      }
      lastErr = err?.message ?? lastErr;
    }
    if (!signedUrl) {
      setError(lastErr ?? 'Could not generate download link.');
      return;
    }
    logAuditEvent('document.download', {
      tableName: 'documents',
      rowId: doc.id,
    }).catch(() => {});
    window.open(signedUrl, '_blank', 'noopener');
  };

  const handleDelete = async (doc: DocumentRow) => {
    if (!confirm(`Remove ${doc.filename}? Your case manager will no longer see this file, and you will need to upload it again.`)) return;
    // Soft-delete via RPC, not a plain update (B12-D2 fix). A direct
    // `.from('documents').update({ deleted_at })` is rejected outright by
    // RLS (42501) even for a client soft-deleting their own upload: Postgres
    // folds the permissive "clients read own documents" SELECT policy's
    // `deleted_at IS NULL` clause into the UPDATE's implicit row-validity
    // check, so setting deleted_at to non-null — the entire point of a
    // soft-delete — makes the resulting row fail that SELECT policy and the
    // write is refused. Confirmed live on staging (20260713000004 migration
    // has the full repro). soft_delete_own_document() does the identical
    // ownership check the RLS policy intended, then writes with elevated
    // privilege to sidestep the self-contradiction.
    const { error: err } = await supabase.rpc('soft_delete_own_document', {
      document_id: doc.id,
    });
    if (err) {
      // Server-side guard (20260714000001): clients cannot remove a document
      // that has already been approved. Map the raw RPC error to friendly copy.
      setError(
        /approved/i.test(err.message)
          ? 'This document has already been approved. Ask your case manager if it needs replacing.'
          : err.message,
      );
      return;
    }
    setDocs((prev) => prev.filter((d) => d.id !== doc.id));
  };

  if (matterLoading) {
    return <div className="text-sm text-slate-500">Loading...</div>;
  }
  if (!activeMatter) {
    return (
      <div className="card p-6 text-sm text-slate-500">
        No active matter yet. We'll email you when your case is set up.
      </div>
    );
  }

  // Build the checklist twice: applicant-side (what the applicant uploads)
  // and sponsor-side (what the sponsor uploads). Sponsor-mode shows ONLY the
  // sponsor list as actionable. Applicant-mode shows applicant items as
  // actionable + sponsor items as greyed/tagged informational rows so the
  // applicant can see the full picture of the matter without being able to
  // upload on the sponsor's behalf.
  const applicantChecklistRaw: DocItem[] = checklistForVisa(
    activeMatter.visa_subclass,
    activeMatter.variant,
    {
      hasMinorDependents,
      answers: intakeAnswers,
      // 600 tourist: an Inviter linked on the matter surfaces the inviter
      // documents immediately, without waiting for the applicant to reach the
      // funding question (Wave 5).
      hasInviterParty: !!activeMatter.sponsor_contact_id,
    },
  );
  // Owner model (Jane's matrix):
  //   applicant -> applicant uploads (actionable in applicant mode)
  //   sponsor   -> sponsor uploads (actionable in sponsor mode; greyed in applicant mode)
  //   staff     -> STAFF-UPLOAD-ONLY (Form 80 / Form 956); never actionable for
  //                applicant or sponsor, but shown to them as a greyed,
  //                "ADI uploads this" awareness row.
  // Applicant view actionable items = owner applicant/undefined, excluding
  // staff-only and sponsor rows (those render in their own sections).
  const applicantOwnedItemsAll = applicantChecklistRaw.filter(
    (c) => (c.owner === 'applicant' || c.owner === undefined) && !c.staffOnly,
  );
  // Two-stage gate (§1.6): before the applicant's stage-1 latch, post-lodgement
  // (stage-2) documents are held back — greyed in their own section and kept
  // out of the checklist counter. Non-partner checklists carry no stage tag, so
  // this filter is a no-op there. Staff/sponsor viewers are never gated.
  const preLatch = !showStaffControls && !stage1LatchedAt;
  const applicantOwnedItems = applicantOwnedItemsAll.filter(
    (c) => !(preLatch && c.stage === 2),
  );
  const postLodgementItems = preLatch
    ? applicantOwnedItemsAll.filter((c) => c.stage === 2)
    : [];
  // Staff-only items surfaced for awareness (greyed, no upload button) on both
  // the applicant and sponsor views.
  const staffOnlyItems = applicantChecklistRaw.filter((c) => c.staffOnly);
  const sponsorOwnedItems = getSponsorDocumentChecklist(
    activeMatter.visa_subclass,
    intakeAnswers,
  ).filter((c) => !c.staffOnly);

  // The "active" checklist for sponsor mode is the sponsor-owned items.
  // For applicant mode, only applicant-owned items count toward the headline
  // progress — sponsor and staff rows are for awareness, not their responsibility.
  const checklist: DocItem[] = isSponsorMode ? sponsorOwnedItems : applicantOwnedItems;
  // Group by category, required first within each.
  const grouped = new Map<string, DocItem[]>();
  for (const c of checklist) {
    const existing = grouped.get(c.category) ?? [];
    existing.push(c);
    grouped.set(c.category, existing);
  }
  const requiredItems = checklist.filter((c) => c.required);
  const total = requiredItems.length;
  // Two progress dimensions:
  //   uploadedCount = required items with at least one (non-deleted) file uploaded
  //   approvedCount = required items where the latest version is `approved`
  // Together these answer "how much of the checklist is in / how much is signed off".
  const uploadedCount = requiredItems.filter((c) => (docsByType.get(c.key)?.length ?? 0) > 0).length;
  const approvedCount = requiredItems.filter((c) => {
    const docsForKey = docsByType.get(c.key) ?? [];
    return docsForKey.some((d) => d.review_status === 'approved');
  }).length;
  // Sponsor rollup for the informational tag in applicant-mode.
  const sponsorUploadedCount = sponsorOwnedItems.filter((c) => (docsByType.get(c.key)?.length ?? 0) > 0).length;
  const sponsorTotal = sponsorOwnedItems.length;
  const uploadedPct = total > 0 ? Math.round((uploadedCount / total) * 100) : 0;
  const approvedPct = total > 0 ? Math.round((approvedCount / total) * 100) : 0;
  // Single "completion %" headline — gamification metric.
  // Approved counts full (1.0); uploaded-but-not-yet-approved counts half (0.5).
  // This rewards first-step action (upload) without letting unreviewed files
  // pretend the matter is fully ready.
  const percentComplete = total > 0
    ? Math.round(((approvedCount + 0.5 * (uploadedCount - approvedCount)) / total) * 100)
    : 0;
  // Choose ring colour by milestone so the number visually celebrates progress.
  const ringTone =
    percentComplete >= 100 ? 'text-green-600 bg-green-50 border-green-200'
      : percentComplete >= 75 ? 'text-emerald-600 bg-emerald-50 border-emerald-200'
      : percentComplete >= 50 ? 'text-[#0B2341] bg-[#EBF7FD] border-[#2CABEA]/40'
      : percentComplete >= 25 ? 'text-amber-700 bg-amber-50 border-amber-200'
      : 'text-slate-600 bg-slate-50 border-slate-200';

  // Task A robustness: in sponsor mode the upload pipeline needs a resolvable
  // contact id to write `documents.contact_id` and the /sponsor/<id>/ storage
  // path. When the sponsor contact wasn't created/linked at matter creation,
  // useMatter's effectiveContactId can't resolve the sponsor and the upload
  // button would silently no-op (handleFileChange early-returns on a null
  // uploadContactId). Detect that and show a clear message instead of a dead
  // button. Root cause is cross-file (CreateMatter/MatterReadiness don't always
  // link sponsor_contact_id) — see report.
  const sponsorUploadBlocked =
    isSponsorMode && (!uploadContactId || !activeMatter.sponsor_contact_id);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-1">Documents</h1>
        <p className="text-slate-500">
          {isSponsorMode
            ? `Your sponsor-side checklist for this ${activeMatter.visa_subclass}${activeMatter.variant ? ` ${activeMatter.variant}` : ''} Partner Visa. Upload the items below; the applicant cannot upload these on your behalf.`
            : <>Your checklist for the {activeMatter.visa_subclass}{activeMatter.variant ? ` ${activeMatter.variant}` : ''} Partner Visa. We tick these off as they come in.</>}
        </p>
      </div>

      {sponsorUploadBlocked && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
          We're still setting up your sponsor profile on this matter, so uploads
          aren't enabled yet. Your case manager will sort this out shortly. Please
          check back, or contact ADI if it's urgent.
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={ALLOWED_INPUT}
        onChange={handleFileChange}
      />

      <div className="card">
        <div className="section">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              {/* Headline % complete — single number for at-a-glance progress. */}
              <div
                className={`shrink-0 grid place-items-center rounded-xl border-2 ${ringTone} w-20 h-20 leading-none`}
                aria-label={`${percentComplete} percent complete`}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold tabular-nums">{percentComplete}<span className="text-base font-semibold">%</span></div>
                  <div className="text-[10px] uppercase tracking-wide font-semibold opacity-80 mt-0.5">Complete</div>
                </div>
              </div>
              <div>
                <h2 className="text-lg font-bold">Document checklist</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {checklist.length} total items
                  {requiredItems.length !== checklist.length ? ` · ${total} required` : ''}
                </p>
              </div>
            </div>
            <div className="text-right text-xs">
              <div className="text-slate-500">
                <span className="font-semibold text-[#0B2341]">{uploadedCount}</span> of {total} uploaded
                <span className="text-slate-400"> · </span>
                <span className="font-semibold text-green-700">{approvedCount}</span> approved
              </div>
            </div>
          </div>

          {/* Stacked progress bar: green segment = approved, blue segment = uploaded-but-not-yet-approved.
              Gives both client and staff a single glance at "where are we, what's still to chase". */}
          <div className="mt-3" aria-label={`Progress: ${uploadedCount} of ${total} required uploaded, ${approvedCount} approved`}>
            <div className="relative h-2 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-[#2CABEA] transition-all"
                style={{ width: `${uploadedPct}%` }}
              />
              <div
                className="absolute inset-y-0 left-0 bg-green-500 transition-all"
                style={{ width: `${approvedPct}%` }}
              />
            </div>
            <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-green-500" /> Approved ({approvedPct}%)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#2CABEA]" /> Uploaded ({uploadedPct}%)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-slate-200" /> Not yet uploaded
              </span>
            </div>
          </div>
        </div>

        {(uploadProgress > 0 || uploadError || uploadInfo) && (
          <div className="section space-y-2">
            {uploadProgress > 0 && (
              <div className="rounded-lg bg-[#EBF7FD] p-3 text-sm flex items-center gap-3">
                <Loader2 className="animate-spin text-[#2CABEA]" size={16} />
                <span className="flex-1">
                  {uploadProgress < 50 ? 'Preparing your file...' : `Uploading... ${uploadProgress}%`}
                </span>
              </div>
            )}
            {uploadError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {uploadError}
              </div>
            )}
            {uploadInfo && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                {uploadInfo}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="section">
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {error}
            </div>
          </div>
        )}

        <div className="section">
          {loading ? (
            <div className="text-sm text-slate-500">Loading documents...</div>
          ) : (
            <div className="space-y-5">
              {Array.from(grouped.entries()).map(([category, items]) => (
                <div key={category}>
                  <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
                    {CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS] ?? category}
                  </h3>
                  <ul className="divide-y divide-slate-100">
                    {items.map((c) => {
                      const uploaded = docsByType.get(c.key) ?? [];
                      const done = uploaded.length > 0;
                      return (
                        <li key={c.key} className="py-3">
                          <div className="flex items-center gap-3">
                            {done ? (
                              <Check size={18} className="text-green-500 flex-shrink-0" />
                            ) : (
                              <Circle size={18} className={`${c.required ? 'text-amber-400' : 'text-slate-300'} flex-shrink-0`} />
                            )}
                            <span className={`flex-1 ${done ? 'text-slate-500' : 'text-slate-800'}`}>
                              {c.label}
                              {c.required && !done && (
                                <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold text-amber-700">Required</span>
                              )}
                              {!c.required && !done && (
                                <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold text-slate-400">Optional</span>
                              )}
                              {c.hint && !done && (
                                <p className="text-xs text-slate-500 font-normal mt-0.5">{c.hint}</p>
                              )}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleUploadClick(c.key)}
                              disabled={sponsorUploadBlocked}
                              className="text-xs text-[#2CABEA] font-semibold hover:underline flex items-center gap-1 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
                              title={sponsorUploadBlocked ? 'Uploads are not enabled until your sponsor profile is set up.' : undefined}
                            >
                              <Upload size={12} /> Upload
                            </button>
                          </div>
                          {uploaded.length > 0 && (
                            <ul className="mt-2 ml-8 space-y-1.5">
                              {uploaded.map((d) => (
                                <li key={d.id} className="text-xs">
                                  <div className="flex items-center gap-2 text-slate-600">
                                    <button
                                      type="button"
                                      onClick={() => setViewingDocId(d.id)}
                                      className="flex-1 text-left truncate text-[#0B2341] hover:text-[#2CABEA] hover:underline"
                                      title="Open preview"
                                    >
                                      {d.filename}
                                    </button>
                                    <ReviewBadge status={d.review_status ?? 'pending'} actuallyStaff={actuallyStaff} />
                                    <span className="text-slate-400">{formatBytes(d.size_bytes)}</span>
                                    <button
                                      type="button"
                                      onClick={() => setViewingDocId(d.id)}
                                      className="text-[#2CABEA] hover:underline flex items-center gap-1"
                                      title="Open preview"
                                    >
                                      <Eye size={11} /> View
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDownload(d)}
                                      className="text-[#2CABEA] hover:underline flex items-center gap-1"
                                    >
                                      <Download size={11} /> Download
                                    </button>
                                    {/* Replace CTA: visible only when staff has flagged this version
                                        as needing a new upload. The new file chains via
                                        previous_version_id; the old row is preserved (compliance). */}
                                    {d.review_status === 'needs_replacement' && (
                                      <button
                                        type="button"
                                        onClick={() => handleReplaceClick(c.key, d.id)}
                                        className="text-amber-700 font-semibold hover:underline flex items-center gap-1"
                                        title="Upload a new version of this document"
                                      >
                                        <RefreshCw size={11} /> Replace
                                      </button>
                                    )}
                                    {/* An approved document is reviewed evidence: only staff may
                                        remove it (guarded server-side in soft_delete_own_document).
                                        Hide the control for clients rather than let them click
                                        into a refusal. */}
                                    {(showStaffControls || d.review_status !== 'approved') && (
                                      <button
                                        type="button"
                                        onClick={() => handleDelete(d)}
                                        className="text-red-500 hover:underline flex items-center gap-1"
                                        title="Remove"
                                      >
                                        <Trash2 size={11} />
                                      </button>
                                    )}
                                  </div>
                                  {d.review_status === 'needs_replacement' && (latestComment[d.id] ?? d.review_note) && (
                                    <div className="mt-1 ml-1 p-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900">
                                      <span className="font-semibold">Latest update from your case manager:</span>{' '}
                                      {latestComment[d.id] ?? d.review_note}
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Applicant-mode informational block: what the sponsor is responsible
          for. Greyed, non-actionable, and does NOT count toward the applicant
          progress bar. The sponsor uploads these from their own portal account.
          Hidden in sponsor-mode (sponsor sees their items as the main list)
          and hidden when the visa has no sponsor-mode flow (e.g. 600-tourist). */}
      {!isSponsorMode && sponsorOwnedItems.length > 0 && (
        <div className="card">
          <div className="section">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-bold">Sponsor uploads</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Your sponsor signs in separately to upload these. They don't
                  count toward your progress bar.
                </p>
              </div>
              <div className="text-xs text-slate-500">
                <span className="font-semibold text-[#0B2341]">{sponsorUploadedCount}</span>
                {' of '}{sponsorTotal} uploaded by sponsor
              </div>
            </div>
          </div>
          <div className="section">
            <ul className="divide-y divide-slate-100">
              {sponsorOwnedItems.map((c) => {
                const uploaded = docsByType.get(c.key) ?? [];
                const done = uploaded.length > 0;
                return (
                  <li
                    key={c.key}
                    className="py-3 opacity-70"
                    title="Sponsor uploads this. Your sponsor signs in separately to add it."
                  >
                    <div className="flex items-center gap-3">
                      {done ? (
                        <Check size={18} className="text-green-500 flex-shrink-0" />
                      ) : (
                        <Circle size={18} className="text-slate-300 flex-shrink-0" />
                      )}
                      <span className="flex-1 text-slate-600">
                        {c.label}
                        <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                          Sponsor uploads this
                        </span>
                        {c.hint && !done && (
                          <p className="text-xs text-slate-500 font-normal mt-0.5">{c.hint}</p>
                        )}
                      </span>
                      {/* No upload button — applicants cannot upload sponsor docs.
                          Clicking the row does nothing; the title attr explains why. */}
                    </div>
                    {/* Staff review path (DOCFB finding 2): staff get the same
                        open/review affordances on sponsor uploads as on
                        applicant ones — previously these rows were tick-only
                        and sponsor docs were reachable only via Prev/Next.
                        Applicants never see file rows here (RLS hides sponsor
                        uploads from them anyway). */}
                    {showStaffControls && uploaded.length > 0 && (
                      <ul className="mt-2 ml-8 space-y-1.5">
                        {uploaded.map((d) => (
                          <li key={d.id} className="text-xs">
                            <div className="flex items-center gap-2 text-slate-600">
                              <button
                                type="button"
                                onClick={() => setViewingDocId(d.id)}
                                className="flex-1 text-left truncate text-[#0B2341] hover:text-[#2CABEA] hover:underline"
                                title="Open preview"
                              >
                                {d.filename}
                              </button>
                              <ReviewBadge status={d.review_status ?? 'pending'} actuallyStaff={actuallyStaff} />
                              <span className="text-slate-400">{formatBytes(d.size_bytes)}</span>
                              <button
                                type="button"
                                onClick={() => setViewingDocId(d.id)}
                                className="text-[#2CABEA] hover:underline flex items-center gap-1"
                                title="Open preview"
                              >
                                <Eye size={11} /> View
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDownload(d)}
                                className="text-[#2CABEA] hover:underline flex items-center gap-1"
                              >
                                <Download size={11} /> Download
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Staff-upload-only awareness block (Jane's matrix: Form 80 / Form 956).
          ADI prepares these post-lodgement and sends for signature, then staff
          upload them. Shown to applicant and sponsor as greyed, non-actionable
          rows so they know these are handled for them. Hidden from the staff's
          own "view as client" preview is NOT needed — staff use the matter
          overview to upload these, not this client-shaped page. */}
      {staffOnlyItems.length > 0 && (
        <div className="card">
          <div className="section">
            <h2 className="text-lg font-bold">ADI uploads these for you</h2>
            <p className="text-sm text-slate-500 mt-1">
              We prepare and lodge these forms on your behalf after lodgement.
              You don't need to upload anything here.
            </p>
          </div>
          <div className="section">
            <ul className="divide-y divide-slate-100">
              {staffOnlyItems.map((c) => {
                const uploaded = docsByType.get(c.key) ?? [];
                const done = uploaded.length > 0;
                return (
                  <li
                    key={c.key}
                    className="py-3 opacity-70"
                    title="ADI staff upload this for you."
                  >
                    <div className="flex items-center gap-3">
                      {done ? (
                        <Check size={18} className="text-green-500 flex-shrink-0" />
                      ) : (
                        <Circle size={18} className="text-slate-300 flex-shrink-0" />
                      )}
                      <span className="flex-1 text-slate-600">
                        {c.label}
                        <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                          ADI uploads this
                        </span>
                        {c.hint && !done && (
                          <p className="text-xs text-slate-500 font-normal mt-0.5">{c.hint}</p>
                        )}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Post-lodgement (stage-2) documents, held back until the pre-lodgement
          intake latches (§1.6). Shown greyed for awareness so the client sees
          the full picture, but not actionable and not counted yet. */}
      {postLodgementItems.length > 0 && (
        <div className="card">
          <div className="section">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Lock size={18} className="text-slate-400" /> Post-lodgement documents
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              These relationship-evidence documents open once you complete the
              pre-lodgement form. You can start gathering them now.
            </p>
          </div>
          <div className="section">
            <ul className="divide-y divide-slate-100">
              {postLodgementItems.map((c) => (
                <li
                  key={c.key}
                  className="py-3 opacity-70"
                  title="Opens after you complete the pre-lodgement form."
                >
                  <div className="flex items-center gap-3">
                    <Lock size={18} className="text-slate-300 flex-shrink-0" />
                    <span className="flex-1 text-slate-600">
                      {c.label}
                      <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                        After lodgement
                      </span>
                      {c.hint && (
                        <p className="text-xs text-slate-500 font-normal mt-0.5">{c.hint}</p>
                      )}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="card p-6 bg-[#EBF7FD] border-[#2CABEA]/30">
        <div className="flex items-start gap-3">
          <FolderOpen className="text-[#2CABEA] flex-shrink-0" size={20} />
          <div className="text-sm text-slate-700">
            <p className="font-semibold text-[#0B2341] mb-1">File limits</p>
            {/* Derived from the same constants the upload validation enforces
                (MAX_FILE_BYTES above, ACCEPTED_FORMATS_LABEL in uploadDocument.ts)
                so this card can never drift from the real limits again. */}
            <p>
              Up to {Math.round(MAX_FILE_BYTES / (1024 * 1024))} MB per file. Accepted formats: {ACCEPTED_FORMATS_LABEL}. If your file is
              larger, compress it or split it into pages. Anything in another format, email your case
              manager directly.
            </p>
          </div>
        </div>
      </div>

      {(() => {
        if (!viewingDocId) return null;
        const idx = docs.findIndex((d) => d.id === viewingDocId);
        if (idx < 0) return null;
        const current = docs[idx];
        // Label lookup spans every checklist section this page knows about
        // (active list + sponsor + staff-only + gated stage-2 rows) so sponsor
        // and staff-uploaded docs opened in the viewer still show their
        // checklist label — previously only the active list was searched and
        // sponsor docs rendered label-less (DOCFB finding 2).
        const matchingChecklistItem = current.document_type
          ? [...checklist, ...sponsorOwnedItems, ...staffOnlyItems, ...postLodgementItems]
              .find((c) => c.key === current.document_type)
          : null;
        return (
          <DocumentViewer
            doc={{
              id: current.id,
              matter_id: activeMatter!.id,
              storage_path: current.storage_path,
              filename: current.filename,
              mime_type: current.mime_type ?? null,
              size_bytes: current.size_bytes ?? null,
              review_status: current.review_status,
              review_note: current.review_note,
            }}
            checklistLabel={matchingChecklistItem?.label ?? null}
            position={{ index: idx, total: docs.length }}
            onPrev={idx > 0 ? () => setViewingDocId(docs[idx - 1].id) : undefined}
            onNext={idx < docs.length - 1 ? () => setViewingDocId(docs[idx + 1].id) : undefined}
            onClose={() => setViewingDocId(null)}
            showReviewControls={showStaffControls}
            reviewerContactId={contact?.id ?? null}
            onReviewSaved={(next) =>
              setDocs((prev) =>
                prev.map((d) =>
                  d.id === next.id
                    ? { ...d, review_status: next.review_status, review_note: next.review_note }
                    : d,
                ),
              )
            }
            onUpdated={(next) =>
              setDocs((prev) =>
                prev.map((d) =>
                  d.id === next.id
                    ? { ...d, storage_path: next.storage_path, filename: next.filename, mime_type: next.mime_type }
                    : d,
                ),
              )
            }
          />
        );
      })()}
    </div>
  );
}

function ReviewBadge({ status, actuallyStaff }: { status: ReviewStatus; actuallyStaff: boolean }) {
  // The internal `reviewing` status is staff-only per
  // src/schema/reference-data/document-review-status.json (client_visible:false).
  // For non-staff viewers, present it as `pending` so internal triage isn't leaked.
  const visibleStatus: ReviewStatus = !actuallyStaff && status === 'reviewing' ? 'pending' : status;
  const meta = statusMeta(visibleStatus);
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${meta.color} ${meta.bg}`}>
      <meta.Icon size={11} /> {meta.label}
    </span>
  );
}


function groupByType(docs: DocumentRow[]): Map<string, DocumentRow[]> {
  const map = new Map<string, DocumentRow[]>();
  for (const d of docs) {
    const k = d.document_type ?? 'other';
    const list = map.get(k) ?? [];
    list.push(d);
    map.set(k, list);
  }
  return map;
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
